import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  DndContext as DndKitContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import type {
  DragItem,
  DragOptionData,
  DropResult
} from '../schemas';
import { useSound } from '../../sounds';

// ============================================
// CONTEXT
// ============================================

interface ExerciseDndContextValue {
  activeItem: DragItem | null;
  isDragging: boolean;
  usedOptionIds: Set<string>; // Source option IDs currently placed in inputs
  droppedSources: Map<string, string>; // inputWidgetId → source drag option ID
  registerDropHandler: (targetId: string, handler: (item: DragItem) => boolean | void) => void;
  unregisterDropHandler: (targetId: string) => void;
}

const ExerciseDndContext = createContext<ExerciseDndContextValue | null>(null);

export function useExerciseDnd() {
  const ctx = useContext(ExerciseDndContext);
  if (!ctx) {
    throw new Error('useExerciseDnd must be used within ExerciseDndProvider');
  }
  return ctx;
}

// Optional hook that doesn't throw - for components that may be outside provider
export function useExerciseDndOptional() {
  return useContext(ExerciseDndContext);
}

// ============================================
// PROVIDER
// ============================================

interface ExerciseDndProviderProps {
  children: React.ReactNode;
  droppedSources: Map<string, string>; // inputWidgetId → source drag option ID
  onDragEnd?: (result: DropResult) => void;
}

export const ExerciseDndProvider: React.FC<ExerciseDndProviderProps> = ({
  children,
  droppedSources,
  onDragEnd,
}) => {
  const [activeItem, setActiveItem] = useState<DragItem | null>(null);
  const [dropHandlers] = useState(() => new Map<string, (item: DragItem) => boolean | void>());
  const playSound = useSound();

  // Collect all source option IDs currently placed in inputs
  const usedOptionIds = useMemo(() => {
    return new Set(droppedSources.values());
  }, [droppedSources]);

  // Sensors with activation constraints to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor)
  );

  const registerDropHandler = useCallback((targetId: string, handler: (item: DragItem) => boolean | void) => {
    dropHandlers.set(targetId, handler);
  }, [dropHandlers]);

  const unregisterDropHandler = useCallback((targetId: string) => {
    dropHandlers.delete(targetId);
  }, [dropHandlers]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const raw = active.data.current as Record<string, unknown>;
    const item: DragItem = raw?.type === 'placed-chip'
      ? { type: 'placed-chip', widgetId: raw.widgetId as string, label: raw.label as string, sourceOptionId: raw.sourceOptionId as string | undefined }
      : raw as DragItem;
    setActiveItem(item);
    playSound('drag-start');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.data.current) {
      const item = active.data.current as DragItem;
      const targetId = String(over.id);

      // Call registered handler for this drop target
      const handler = dropHandlers.get(targetId);
      if (handler && handler(item) !== false) {
        playSound('drag-drop');
      }

      // Notify parent
      onDragEnd?.({ targetId, item });
    }

    setActiveItem(null);
  };

  const handleDragCancel = () => {
    setActiveItem(null);
    playSound('drag-cancel');
  };

  return (
    <ExerciseDndContext.Provider
      value={{
        activeItem,
        isDragging: activeItem !== null,
        usedOptionIds,
        droppedSources,
        registerDropHandler,
        unregisterDropHandler,
      }}
    >
      <DndKitContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}

        {/* Drag overlay - shows what's being dragged */}
        <DragOverlay dropAnimation={null}>
          {activeItem && <DragOverlayContent item={activeItem} />}
        </DragOverlay>
      </DndKitContext>
    </ExerciseDndContext.Provider>
  );
};

// ============================================
// DRAG OVERLAY - Customize appearance per type
// ============================================

const DragOverlayContent: React.FC<{ item: DragItem }> = ({ item }) => {
  switch (item.type) {
    case 'drag-option': {
      const data = item.data as DragOptionData;
      return (
        <div className="drag-overlay-option">
          {data.label}
        </div>
      );
    }

    case 'placed-chip': {
      return (
        <div className="drag-overlay-option">
          {item.label}
        </div>
      );
    }
    // Future: Add cases for other drag types
    // case 'ruler-handle':
    //   return <RulerHandleOverlay data={item.data} />;

    default:
      return null;
  }
};
